import React, { useCallback, useEffect, useState } from 'react';
import { Button, TopBar, Spinner, FullScreen, Field } from '../components/ui';
import { rupee } from '../theme';
import { useToast } from '../context/ToastContext';
import api from '../api/client';
import { payForBooking } from '../api/payment';
import StaffProfile from './StaffProfile';
import { useAuth } from '../context/AuthContext';

// Build a dialable tel: URL in Indian format (+91…).
const telUrl = (num) => {
  const raw = String(num || '').trim();
  if (raw.startsWith('+')) return `tel:${raw.replace(/[^\d+]/g, '')}`;
  let d = raw.replace(/\D/g, '');
  if (d.length === 10) d = '91' + d;
  return `tel:+${d}`;
};
// Pretty display: +91 98765 43210
const prettyPhone = (num) => {
  let d = String(num || '').replace(/\D/g, '');
  if (d.length > 10) d = d.slice(-10);
  return d.length === 10 ? `+91 ${d.slice(0, 5)} ${d.slice(5)}` : num;
};

// Ordered stages for the progress tracker.
const FLOW = [
  { key: 'query', label: 'Request' },
  { key: 'appointment', label: 'Appt.' },
  { key: 'interview', label: 'Interview' },
  { key: 'security', label: 'Security' },
  { key: 'trial', label: 'Trial' },
  { key: 'active', label: 'Active' },
];
const stageIndex = (stage) => {
  if (stage === 'trial_ended') return FLOW.findIndex((f) => f.key === 'trial');
  if (stage === 'sourcing') return FLOW.findIndex((f) => f.key === 'appointment');
  if (stage === 'final') return FLOW.findIndex((f) => f.key === 'interview');
  const i = FLOW.findIndex((f) => f.key === stage);
  return i < 0 ? 0 : i;
};

const Waiting = ({ emoji, title, text }) => (
  <div style={{ textAlign: 'center', padding: '10px 0' }}>
    <div style={{ fontSize: 40 }}>{emoji}</div>
    <div style={{ fontSize: 16, fontWeight: 800, marginTop: 10 }}>{title}</div>
    <p className="muted" style={{ marginTop: 8, lineHeight: 1.5 }}>{text}</p>
  </div>
);

function Candidate({ c, selected, onSelect, onView, pickable }) {
  return (
    <div
      onClick={pickable ? onSelect : undefined}
      className="row"
      style={{
        gap: 12, padding: 12, borderRadius: 'var(--r-md)', marginBottom: 8,
        border: `1px solid ${selected ? 'var(--primary)' : 'var(--border)'}`,
        background: selected ? 'var(--primary-soft)' : 'var(--surface)',
        cursor: pickable ? 'pointer' : 'default',
      }}
    >
      <span className="avatar" style={{ width: 44, height: 44, background: 'var(--bg)', color: 'var(--text)' }}>
        {c.profilePhoto ? <img src={c.profilePhoto} alt="" /> : (c.name || '?').charAt(0)}
      </span>
      <div className="grow">
        <div style={{ fontSize: 14, fontWeight: 700 }}>{c.name || 'Worker'}</div>
        <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
          {(c.skills || []).join(', ') || 'Verified'} · ⭐ {c.rating || '—'} · {c.experienceYears || 0}y exp
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onView(); }}
          style={{ fontSize: 12, color: 'var(--primary)', fontWeight: 700, marginTop: 4 }}
        >
          View {pickable ? 'profile' : 'details'} ›
        </button>
      </div>
      {pickable && <span className={`radio ${selected ? 'radio--on' : ''}`} style={{ width: 22, height: 22 }} />}
    </div>
  );
}

export default function BookingProcess({ bookingId, onClose }) {
  const { toast, confirm } = useToast();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState(null);
  const [viewStaff, setViewStaff] = useState(null);
  const [terms, setTerms] = useState('');
  const [showTerms, setShowTerms] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [bank, setBank] = useState({ accountName: '', accountNumber: '', ifsc: '', bankName: '', upi: '' });

  const load = useCallback(async () => {
    try {
      const d = await api.get(`/bookings/${bookingId}`);
      setData(d);
      if (d.booking.selectedCandidate) setPicked(d.booking.selectedCandidate);
    } catch (e) {
      setData({ error: true });
    }
  }, [bookingId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/settings/public').then((d) => setTerms(d?.termsAndConditions || '')).catch(() => {});
  }, []);

  const b = data?.booking;
  const due = data?.due;
  const curIdx = b ? stageIndex(b.stage) : 0;
  // Prefer the value from `due` (always fresh) then the populated package.
  const shortDesc = due?.shortDescription || b?.package?.shortDescription;

  const payDue = async () => {
    setBusy(true);
    const pay = await payForBooking(bookingId, {
      name: due?.label,
      prefill: { name: user?.name, mobile: user?.mobile, email: user?.email },
    });
    if (pay.success) {
      try { await api.post(`/bookings/${bookingId}/pay-stage`, pay.fields || {}); await load(); }
      catch (err) { toast(err.message, { title: 'Could not confirm payment', tone: 'error' }); }
    } else {
      toast(pay.error || 'Please try again.', { title: 'Payment failed', tone: 'error' });
    }
    setBusy(false);
  };

  const acceptAndPay = async () => {
    setBusy(true);
    try { await api.post(`/bookings/${bookingId}/accept-terms`); } catch (e) { /* ignore */ }
    setShowTerms(false);
    await payDue();
  };

  const changeCandidate = async () => {
    const ok = await confirm({
      title: 'Change worker?',
      message: 'We will shortlist new workers. Your security deposit stays — no new deposit needed.',
      confirmLabel: 'Change worker',
    });
    if (!ok) return;
    setBusy(true);
    try { await api.post(`/bookings/${bookingId}/change-candidate`); await load(); }
    catch (err) { toast(err.message, { tone: 'error' }); }
    setBusy(false);
  };

  const submitCancel = async () => {
    if (!bank.accountNumber || !bank.ifsc) {
      return toast('Please enter your account number and IFSC for the refund', { tone: 'error' });
    }
    setBusy(true);
    try {
      const d = await api.post(`/bookings/${bookingId}/cancel`, { reason: cancelReason, bank });
      setShowCancel(false);
      toast(`Refund of ${rupee(d.refund.refundAmount)} will be settled to your account in ${d.refund.settledEta}.`, {
        title: 'Cancelled', tone: 'success', duration: 6000,
      });
      await load();
    } catch (err) {
      toast(err.message, { title: 'Could not cancel', tone: 'error' });
    }
    setBusy(false);
  };

  const confirmInterview = async () => {
    setBusy(true);
    try { await api.post(`/bookings/${bookingId}/confirm-interview`, { candidateId: picked }); await load(); }
    catch (err) { toast(err.message, { title: 'Could not confirm', tone: 'error' }); }
    setBusy(false);
  };

  const requestMore = async () => {
    const ok = await confirm({
      title: 'Request more workers?',
      message: 'We will shortlist a fresh set of candidates for you to interview.',
      confirmLabel: 'Reject all & request more',
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try { await api.post(`/bookings/${bookingId}/request-more`); setPicked(null); await load(); }
    catch (err) { toast(err.message, { title: 'Could not request', tone: 'error' }); }
    setBusy(false);
  };

  const amountRow = (label, amount) => (
    <div className="row-between" style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
      <span className="muted" style={{ fontSize: 14 }}>{label}</span>
      <span style={{ fontSize: 22, fontWeight: 900, color: 'var(--primary)' }}>{rupee(amount)}</span>
    </div>
  );

  const descBanner = (text) => (
    <div style={{ background: 'var(--primary-soft)', borderRadius: 'var(--r-md)', padding: 12, marginBottom: 14 }}>
      <span style={{ fontSize: 12.5, color: 'var(--primary)', fontWeight: 600, lineHeight: 1.45 }}>ℹ️&nbsp; {text}</span>
    </div>
  );

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <TopBar title="Booking Process" onClose={onClose} />

      {!data ? (
        <Spinner />
      ) : data.error || !b ? (
        <div className="loading-center"><span className="muted">Could not load this booking.</span></div>
      ) : (
        <div style={{ padding: 18, paddingBottom: 40 }}>
          <h2 style={{ fontSize: 20, fontWeight: 900, margin: 0 }}>{b.packageTitle}</h2>
          <div style={{ fontSize: 13, color: 'var(--primary)', fontWeight: 700, marginTop: 2, marginBottom: 16 }}>{b.bookingCode}</div>

          {/* Progress tracker */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 18 }}>
            {FLOW.map((f, i) => (
              <div key={f.key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <span style={{
                  width: 26, height: 26, borderRadius: '50%',
                  background: i < curIdx ? 'var(--success)' : i === curIdx ? 'var(--primary)' : '#E6E8EF',
                  color: i <= curIdx ? '#fff' : 'var(--faint)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 800,
                }}>{i < curIdx ? '✓' : i + 1}</span>
                <span style={{
                  fontSize: 9.5, marginTop: 4, fontWeight: i === curIdx ? 800 : 600,
                  color: i === curIdx ? 'var(--primary)' : 'var(--faint)', textAlign: 'center',
                }}>{f.label}</span>
              </div>
            ))}
          </div>

          {/* Current-stage action card */}
          <div className="card" style={{ marginBottom: 14 }}>
            {b.stage === 'query' && (
              <Waiting emoji="📝" title="Request received"
                text="Our team is reviewing your request and will shortlist workers shortly. You'll be notified here." />
            )}

            {b.stage === 'appointment' && due && (
              <>
                <div style={{ fontSize: 16, fontWeight: 800 }}>Pay the appointment fee</div>
                <p className="muted" style={{ marginTop: 8, lineHeight: 1.5 }}>
                  We've shortlisted {(b.candidates || []).length || 'some'} worker(s) for you. Pay the appointment fee to unlock interviews &amp; the contact number.
                </p>

                {(b.candidates || []).length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div className="muted" style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 8 }}>Shortlisted for you</div>
                    {b.candidates.map((c) => (
                      <Candidate key={c._id} c={c} onView={() => setViewStaff(c._id)} pickable={false} />
                    ))}
                    <p className="faint" style={{ fontSize: 11.5, marginTop: 4 }}>
                      🔒 Interview &amp; contact number unlock after the appointment fee.
                    </p>
                  </div>
                )}

                {shortDesc ? descBanner(shortDesc) : null}
                {amountRow(due.label, due.amount)}
                <Button title={busy ? 'Processing…' : `Pay ${rupee(due.amount)}`} onClick={payDue} disabled={busy} style={{ marginTop: 12 }} />
              </>
            )}

            {b.stage === 'sourcing' && (
              <Waiting emoji="🔎" title="Finding workers for you"
                text="Our team is shortlisting verified workers for your requirement. You'll be notified when they're ready to interview." />
            )}

            {b.stage === 'interview' && (
              <>
                <div style={{ fontSize: 16, fontWeight: 800 }}>Interview your candidates</div>
                {b.interviewNumber ? (
                  <a href={telUrl(b.interviewNumber)} className="row" style={{
                    gap: 12, background: 'var(--primary-soft)', borderRadius: 'var(--r-md)',
                    padding: 14, margin: '12px 0',
                  }}>
                    <span style={{ fontSize: 18 }}>📞</span>
                    <div className="grow">
                      <div style={{ fontSize: 12, color: 'var(--primary)', fontWeight: 600 }}>Call to arrange the interview</div>
                      <div style={{ fontSize: 16, fontWeight: 900, marginTop: 2 }}>{prettyPhone(b.interviewNumber)}</div>
                    </div>
                    <span style={{ color: 'var(--primary)', fontWeight: 800 }}>Call</span>
                  </a>
                ) : null}

                {(b.candidates || []).map((c) => (
                  <Candidate
                    key={c._id} c={c} pickable selected={picked === c._id}
                    onSelect={() => setPicked(c._id)} onView={() => setViewStaff(c._id)}
                  />
                ))}

                <Button title={busy ? 'Confirming…' : 'Confirm selection'} onClick={confirmInterview}
                  disabled={busy || !picked} style={{ marginTop: 12 }} />
                <button onClick={requestMore} disabled={busy} style={{
                  marginTop: 12, width: '100%', padding: '10px 0',
                  fontSize: 13, fontWeight: 700, color: 'var(--danger)',
                }}>
                  Didn't like any? Reject all &amp; request more
                </button>
                <p className="faint center" style={{ fontSize: 11.5, marginTop: 10 }}>
                  Pick the worker you liked after interviewing. Our team finalizes them next.
                </p>
              </>
            )}

            {b.stage === 'final' && (
              <Waiting emoji="📝" title="Finalizing your worker"
                text="We're confirming your selected worker. You'll then pay the security deposit to start the trial." />
            )}

            {b.stage === 'security' && due && (
              <>
                <div style={{ fontSize: 16, fontWeight: 800 }}>Pay security deposit</div>
                <p className="muted" style={{ marginTop: 8, lineHeight: 1.5 }}>
                  Your worker {b.staff?.name ? `(${b.staff.name}) ` : ''}is ready. Pay the refundable security deposit to begin the trial period.
                </p>
                {due.description ? descBanner(due.description) : null}
                {amountRow(due.label, due.amount)}
                <Button title={busy ? 'Processing…' : `Pay ${rupee(due.amount)}`} onClick={payDue} disabled={busy} style={{ marginTop: 12 }} />
              </>
            )}

            {b.stage === 'trial' && (
              <Waiting emoji="🧪" title="Trial period running"
                text={b.trialEndsAt
                  ? `Your trial runs until ${new Date(b.trialEndsAt).toLocaleDateString('en-IN')}. We'll remind you when it's time to continue.`
                  : 'Your trial period is active.'} />
            )}

            {b.stage === 'trial_ended' && (
              <>
                <div style={{ fontSize: 16, fontWeight: 800 }}>Your trial has ended</div>
                <p className="muted" style={{ marginTop: 8, lineHeight: 1.5 }}>
                  Choose how you'd like to proceed with {b.staff?.name || 'your worker'}.
                </p>
                {due?.description ? descBanner(due.description) : null}

                {[
                  { icon: '✅', title: 'Continue service', sub: `Pay ${rupee(due?.amount || 0)} to keep this worker.`, onClick: () => setShowTerms(true) },
                  { icon: '🔁', title: 'Change the worker', sub: 'Interview new candidates — no new deposit.', onClick: changeCandidate },
                  { icon: '🚫', title: 'Cancel & refund', sub: 'Get your security deposit back (minus usage).', onClick: () => setShowCancel(true), danger: true },
                ].map((o) => (
                  <button key={o.title} onClick={o.onClick} disabled={busy} className="row" style={{
                    width: '100%', textAlign: 'left', gap: 12, marginTop: 12, padding: 14,
                    border: `1px solid ${o.danger ? 'var(--danger)' : 'var(--border)'}`,
                    borderRadius: 'var(--r-md)',
                  }}>
                    <span style={{ fontSize: 22 }}>{o.icon}</span>
                    <span className="grow">
                      <span style={{ display: 'block', fontSize: 14.5, fontWeight: 800, color: o.danger ? 'var(--danger)' : 'var(--text)' }}>{o.title}</span>
                      <span className="muted" style={{ display: 'block', fontSize: 12, marginTop: 2 }}>{o.sub}</span>
                    </span>
                    <span className="faint" style={{ fontSize: 20 }}>›</span>
                  </button>
                ))}
              </>
            )}

            {b.stage === 'active' && (
              <Waiting emoji="✅" title="Service active"
                text={`Your ${b.packageTitle} is active${b.staff?.name ? ` with ${b.staff.name}` : ''}. Thank you!`} />
            )}

            {b.stage === 'cancelled' && (
              <Waiting emoji="🚫" title="Booking cancelled"
                text={b.cancellation
                  ? `Refund of ${rupee(b.cancellation.refundAmount)} will be settled in ${b.cancellation.settledEta}.`
                  : 'This booking was cancelled.'} />
            )}
          </div>

          {/* Assigned worker */}
          {b.staff && ['security', 'trial', 'trial_ended', 'active'].includes(b.stage) && (
            <div className="card row" style={{ gap: 12, marginBottom: 14, cursor: 'pointer' }}
              onClick={() => setViewStaff(typeof b.staff === 'string' ? b.staff : b.staff._id)}>
              <span className="avatar" style={{ width: 44, height: 44, background: 'var(--bg)', color: 'var(--text)' }}>
                {b.staff.profilePhoto ? <img src={b.staff.profilePhoto} alt="" /> : (b.staff.name || '?').charAt(0)}
              </span>
              <div className="grow">
                <div style={{ fontSize: 14, fontWeight: 700 }}>{b.staff.name || 'Your worker'}</div>
                <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>Your assigned worker · ⭐ {b.staff.rating || '—'}</div>
              </div>
              <span style={{ fontSize: 12, color: 'var(--primary)', fontWeight: 700 }}>View profile ›</span>
            </div>
          )}

          {/* Payments made */}
          {(b.payments || []).length > 0 && (
            <div className="card">
              <div style={{ fontSize: 16, fontWeight: 800 }}>Payments</div>
              {b.payments.map((p, i) => (
                <div key={i} className="row-between" style={{ padding: '6px 0' }}>
                  <span className="muted" style={{ fontSize: 13, textTransform: 'capitalize' }}>{p.type}</span>
                  <span style={{ fontSize: 13.5, fontWeight: 700 }}>{rupee(p.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <FullScreen open={!!viewStaff} onClose={() => setViewStaff(null)}>
        {viewStaff && <StaffProfile staffId={viewStaff} onClose={() => setViewStaff(null)} />}
      </FullScreen>

      {/* Terms & conditions before continuing */}
      <FullScreen open={showTerms} onClose={() => setShowTerms(false)}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
          <TopBar title="Terms & Conditions" onClose={() => setShowTerms(false)} />
          <div style={{ flex: 1, padding: 20, whiteSpace: 'pre-wrap', fontSize: 13.5, lineHeight: 1.6 }}>
            {terms || 'By continuing you agree to our service terms & conditions.'}
          </div>
          <div style={{ padding: 18, paddingBottom: 'calc(18px + var(--sab))' }}>
            <p className="faint center" style={{ fontSize: 11.5, marginBottom: 10 }}>
              You'll pay {rupee(due?.amount || 0)} to continue after accepting.
            </p>
            <Button title={busy ? 'Processing…' : 'I Accept & Continue to Pay'} onClick={acceptAndPay} disabled={busy} />
          </div>
        </div>
      </FullScreen>

      {/* Cancel + refund bank details */}
      <FullScreen open={showCancel} onClose={() => setShowCancel(false)}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
          <TopBar title="Cancel & Refund" onClose={() => setShowCancel(false)} />
          <div style={{ padding: 18, paddingBottom: 40 }}>
            <p className="muted" style={{ marginTop: 0, lineHeight: 1.5 }}>
              Your security deposit will be refunded (minus the per-day usage charge) to the account below within 6–8 business days.
            </p>
            {[['accountName', 'Account holder name'], ['accountNumber', 'Account number'], ['ifsc', 'IFSC code'], ['bankName', 'Bank name'], ['upi', 'UPI (optional)']].map(([k, label]) => (
              <Field
                key={k} label={label} placeholder={label} value={bank[k]}
                inputMode={k === 'accountNumber' ? 'numeric' : undefined}
                onChange={(e) => setBank({ ...bank, [k]: k === 'ifsc' ? e.target.value.toUpperCase() : e.target.value })}
              />
            ))}
            <Field
              as="textarea" label="Feedback (why are you cancelling?)" placeholder="Optional feedback…"
              value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
            />
            <Button title={busy ? 'Cancelling…' : 'Confirm Cancellation'} onClick={submitCancel} disabled={busy} style={{ marginTop: 18 }} />
          </div>
        </div>
      </FullScreen>
    </div>
  );
}
